import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { AuthToken } from '../../api/responses/Auth/auth-token';
import { LoginResponse } from '../../api/responses/Auth/login-response';
import { UserInfoResponse } from '../../api/responses/User/user-info-response';
import { StorageService } from '@portal/shared/services/storage.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly authObjPath = 'authState';
    readonly currentUserPath = 'currentUser';
    readonly lastUserPath = 'lastUser';

    get loginStatus(): Observable<boolean> {
        return this.isLoggedIn.asObservable();
    }

    constructor(
        private router: Router,
        private _storageService: StorageService
    ) {
        this.setIsLoggedInFromStorage();
        this.listenLoginStatusOnOtherTabs();
    }

    private listenLoginStatusOnOtherTabs() {
        addEventListener(
            'storage', event => {
                if (event.key === this.authObjPath) {
                    if (event.newValue) {
                        this.isLoggedIn.next(true);
                        this.router.navigate(['/']);
                    } else {
                        this.isLoggedIn.next(false);
                        this.router.navigate(['/sign-in']);
                    }
                }
            }, false
        );
    }

    private setIsLoggedInFromStorage() {
        const isLoggedIn = this.verifyAuthenticated();
        this.isLoggedIn.next(isLoggedIn);
        return isLoggedIn;
    }

// -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set authToken(token: AuthToken) {
        this._storageService.setObject(this.authObjPath, token);
    }

    set user(user: UserInfoResponse) {
        this._storageService.setObject(this.currentUserPath, user);
        this._storageService.setObject(this.lastUserPath, user);
    }


    get accessToken(): string {
        return this._storageService.getAccessToken();
    }

    getAuth(): AuthToken {
        return this._storageService.getObject(this.authObjPath);
    }

    get refreshToken(): string {
        return this._storageService.getRefreshToken();
    }

    get isAuthenticated(): boolean {
        return this.isLoggedIn.getValue();
    }

    get user(): UserInfoResponse | null {
        return this._storageService.getObject(this.currentUserPath);
    }

    setUserLoggedIn(response: LoginResponse) {
        const lastStorageUser = this._storageService.getLastUser();
        if (!lastStorageUser || lastStorageUser.id !== response.user.id ) {
            this._storageService.clearPastUserData();
        }
        this.authToken = response.token;
        this.user = response.user;
        this.isLoggedIn.next(true);
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('authState');
        // Set the authenticated flag to false
        this.isLoggedIn.next(false);
        // Return the observable
        return of(true);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this.isAuthenticated) {
            return of(true);
        }

        // If there is no recoverable session, force sign-in
        if (!this.hasRecoverableSession()) {
            return of(false);
        }

        // Let the interceptor recover the access token using refresh token if needed
        this.setIsLoggedIn(true);
        return of(true);
    }

    isTokenExpired(): boolean {
        return AuthUtils.isTokenExpired(this.accessToken);
    }

    private verifyAuthenticated(): boolean {
        const token = this.accessToken;

        if (token && !AuthUtils.isTokenExpired(token)) {
            return true;
        }

        if (this.refreshToken) {
            return true;
        }

        return false;
    }

    setIsLoggedIn(value: boolean) {
        this.isLoggedIn.next(value);
    }

    private hasRecoverableSession(): boolean {
        return !!this.accessToken || !!this.refreshToken;
    }
}
